import digitNumber from '@utils/digitNumber';
import { Button, Form, Input, Select, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { senderAccountsModalOpenAC } from '@store/reducers/accounts';
import { electricClientInfoAC, electricQueryAC, isConfirmPaymentModalOpenAC } from '@store/reducers/payments';
import { deadlineAC } from '@store/reducers/transactions';
import electricCities from '@assets/constants/electricCities';

import { getElectricClientInfoApi } from '@http/payments';
import { useState } from 'react';
import { debounce } from '@utils/debounce';

const { TextArea } = Input;

const ElectricForm = ({ form, senderAccountCurrency }: any) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userInfo = useAppSelector(state => state.appReducer.user);
  const senderAccountBalance = useAppSelector(state => state.accountsReducer.senderAccountBalance);
  const electricQuery = useAppSelector(state => state.paymentsReducer.electricQuery);
  const electricClientInfo = useAppSelector(state => state.paymentsReducer.electricClientInfo);

  const [isAccountValid, setIsAccountValid] = useState(true);

  const submit = (values: any) => {
    dispatch(
      electricQueryAC({
        ...electricQuery,
        ...values,
        amount: values.sending_amount,
        receiving_ac: values.account,
        payer: userInfo?.mobile_phone,
        mobile: userInfo?.mobile_phone,
      })
    );

    if (+values.amount > +senderAccountBalance) {
      form.setFields([
        {
          name: 'sending_ac',
          errors: [t('Insufficient funds in the account')],
        },
      ]);
    } else {
      dispatch(isConfirmPaymentModalOpenAC(true));
      dispatch(deadlineAC(Date.now() + 1000 * 30));
    }
  };

  const getElectricClientInfo = (value: any) => {
    if (value.length >= 7 && value !== '' && value !== undefined) {
      const payload = {
        account: value,
        code: electricQuery.code,
        article: electricQuery.article,
      };

      debouncedFn(payload);
    }
  };

  const debouncedFn = debounce((payload: any) => {
    dispatch(getElectricClientInfoApi(payload)).then(data => {
      if (data?.name) {
        setIsAccountValid(true);
        dispatch(electricClientInfoAC(data));
        dispatch(electricQueryAC({ ...electricQuery, address: data.address, name: data.name, sector: data.sector }));
      } else {
        setIsAccountValid(false);
        dispatch(electricClientInfoAC({}));
      }
    });
  }, 1000);

  const pickRegion = (item: any) => {
    dispatch(
      electricQueryAC({
        ...electricQuery,
        article: String(item?.article),
        code: String(item?.code),
        type: String(item?.type),
      })
    );
  };

  const isRegionPicked = () => electricQuery.article;

  return (
    <Form form={form} name='Electric' onFinish={submit}>
      <p className='text-base color-active font-semibold mb-4'>{t('Payment information')}</p>
      <p className='text-bs color-title md:hidden'>{t('Select region')}:</p>
      <div className='md:grid grid-cols-7'>
        <p className='col-span-2 text-bs color-title max-md:hidden'>{t('Select region')}:</p>
        <div className='col-span-5'>
          <Form.Item name='region' className='m-0' rules={[{ required: true, message: `${t('Required')}` }]}>
            <Select
              size='large'
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp='children'
              filterOption={(input, option: any) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={electricCities}
              onChange={(value, item) => pickRegion(item)}
            />
          </Form.Item>
        </div>
      </div>
      <div className='md:grid grid-cols-7 mt-7'>
        <p className='col-span-2 text-bs color-title max-md:hidden'>{t('From account')}:</p>
        <div className='col-span-5'>
          <Form.Item className='m-0' name='sending_ac' rules={[{ required: true, message: `${t('Required')}` }]}>
            <Space.Compact size='large' className='w-full'>
              <Input disabled style={{ width: 60 }} size='large' value={senderAccountCurrency} />
              <Input
                value={electricQuery.sending_ac}
                placeholder={`${t('Select account')}`}
                onClick={() => dispatch(senderAccountsModalOpenAC(true))}
                size='large'
                style={{ width: '100%' }}
                type='text'
                maxLength={20}
              />
            </Space.Compact>
          </Form.Item>
          {senderAccountBalance ? (
            <p className='text-bs color-title font-medium text-end mt-2'>
              {t('Balance')}: {digitNumber(senderAccountBalance)} {senderAccountCurrency}
            </p>
          ) : null}
        </div>
      </div>
      <p className='text-bs color-title mt-5 md:hidden'>{t('Personal account number')}:</p>
      <div className='md:grid grid-cols-7 md:mt-7'>
        <p className='col-span-2 text-bs color-title max-md:hidden'>{t('Personal account number')}:</p>
        <div className='col-span-5'>
          <Form.Item
            validateStatus={isAccountValid ? 'success' : 'error'}
            className='m-0'
            name='account'
            rules={[
              { required: true, message: `${t('Required')}` },
              { min: 7, message: `${t('Account must contain at least 7 digits')}` },
            ]}
          >
            <Input
              placeholder={`${t('Enter Account')}`}
              size='large'
              style={{ width: '100%' }}
              type='text'
              maxLength={20}
              onChange={e => getElectricClientInfo(e.target.value)}
              disabled={!isRegionPicked()}
            />
          </Form.Item>
          {electricClientInfo?.name ? (
            <div className='bg-slate-50 py-3 px-5 mt-2'>
              <p className='text-bs text-slate-500 font-medium text-end mt-2'>
                {t('Balance')}: <b className='color-black'>{electricClientInfo.offsaldo}</b>
              </p>
              <p className='text-bs text-slate-500 font-medium text-end mt-2'>
                {t('Address')}: <b>{electricClientInfo.address}</b>
              </p>
              <p className='text-bs text-slate-500 font-medium text-end mt-2'>
                {t('Name')}: <b> {electricClientInfo.name}</b>
              </p>
              <p className='text-bs text-slate-500 font-medium text-end mt-2'>
                {t('Indication')}: <b>{electricClientInfo.present}</b>
              </p>
            </div>
          ) : null}

          {!isAccountValid ? (
            <p className='text-sm text-red-500 font-medium text-end mt-2'>{t('Account does not found')}</p>
          ) : null}
        </div>
      </div>
      <p className='text-bs color-title mt-5 md:hidden'>{t('Transfer amount')}:</p>
      <div className='md:grid grid-cols-7 md:mt-7'>
        <p className='col-span-2 text-bs color-title max-md:hidden'>{t('Transfer amount')}:</p>
        <div className='col-span-5'>
          <Form.Item
            className='m-0'
            name='sending_amount'
            rules={[
              { required: true, message: `${t('Required')}` },
              {
                pattern: /^[0-9]\d*(?:[.]\d{1,2})?$/,
                message: `${t('Wrong amount')}`,
              },
              {
                pattern: /^[1-9]\d*/,
                message: `${t('Amount must be more 1')}`,
              },
            ]}
          >
            <Space.Compact size='large' className='w-full'>
              <Input disabled style={{ width: 60 }} size='large' value={senderAccountCurrency} />
              <Input
                min={0}
                maxLength={12}
                placeholder={`${t('Enter amount')}`}
                size='large'
                style={{ width: '100%' }}
                type='text'
              />
            </Space.Compact>
          </Form.Item>
        </div>
      </div>
      <div className='mt-7'>
        <p className='text-base color-title'>{t('Narrative')}:</p>
        <Form.Item
          className='m-0'
          name='narrative'
          rules={[
            { required: true, message: `${t('Required')}` },
            {
              pattern: /^(?=.*\S).*/,
              message: `${t('Enter symbol only')}`,
            },
          ]}
        >
          <TextArea maxLength={250} rows={4} />
        </Form.Item>
      </div>
      <div className='flex justify-end mt-5'>
        <Button type='primary' htmlType='submit' disabled={!isAccountValid}>
          {t('Confirm')}
        </Button>
      </div>
    </Form>
  );
};

export default ElectricForm;
